/* eslint-disable react/prop-types, react/destructuring-assignment */
import { Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { ModalSidebar } from '../../../../components/ModalSidebar'
import { ToastContext } from '../../../../context/ToastContext'
import { assignLicenseToEmployees, unassignLicenseFromEmployees, updateEmployee } from '../../../../crud/employees.crud'
import { updateUserProperties } from '../../../../crud/engage.crud'
import { createMgmTask, createTask } from '../../../../crud/integration.crud'
import { createHubTask, pollTaskStatus } from '../../../../crud/integrationHub.crud'
import useAllEmployees from '../../../../hooks/useAllEmployees'
import useCustomerLicenses from '../../../../hooks/useCustomerLicenses'
import useFreshEmployeeData from '../../../../hooks/useFreshEmployeeData'
import useGroupsFetch from '../../../../hooks/useGroupsFetch'
import CopyButton from '../../../../partials/content/CopyButton'
import { adaptGroupsToStandardFormat } from '../../../../util/groups.util'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../util/hub-task-management/hub.constants'
import {
  getGwsDirectoryUserId,
  getGwsLicenseSkuIds,
  getGwsLicenseUserId,
  isGwsDirectoryEmployee,
} from '../../../../util/employeeLicenses.util'
import { getEmployeeFullName } from '../../../../util/table.util'
import { monitorTask } from '../../../../util/tasks.util'
import { actions as GWS_ACTIONS } from '../../engage/integrations/gws/actions'
import { GWS } from '../../engage/utils'
import { resolveDirectoryIntegrationForModal } from '../../utils/integrations'
import { FORM_FIELDS } from './constants'
import EditFormContent from './EditFormContent/EditFormContent'
import { getAssignedLicenseIds, getInitialValues, getValidationSchema, toApiPayload } from './formUtils'
import { submitGwsPrimaryEmailChange } from './updateGwsPrimaryEmail'
import useGwsLicenseCatalog from './useGwsLicenseCatalog'
import { getEditModalTabs, getEmployeeInitials } from './utils'

/** Employee fields compared to decide when to re-init form (job + address). */
const REINIT_COMPARE_FIELDS = [
  'department',
  'job_title',
  'companyName',
  'officeLocation',
  'employeeType',
  'hireDate',
  'streetAddress',
  'city',
  'state',
  'postalCode',
  'country',
]

const valueChanged = (a, b) => (a == null && b == null ? false : a !== b)

function EditEmployeeDataModal(props) {
  const {
    show,
    onHide,
    onUpdate,
    employee: tableRowEmployee,
    selectedCustomer,
    intl,
    headerBackButtonAction,
    allEmployeesGroups = [],
    allEmployees: propAllEmployees = [],
    selectedIntegration: selectedIntegrationFromPage,
    selectedIntegrationType: selectedIntegrationTypeFromPage,
  } = props

  const [submitStatus, setSubmitStatus] = useState('idle')
  const [submitError, setSubmitError] = useState(null)
  const [formInitialValues, setFormInitialValues] = useState({})
  const [licenseBaseline, setLicenseBaseline] = useState([])
  const lastEmployeeIdRef = useRef(null)
  const lastEmployeeDataRef = useRef(null)

  const [, setToastInfo] = useContext(ToastContext)
  const currentUser = useSelector(({ user }) => user.data)
  const reduxSelectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const reduxIntegrations = useSelector(({ integrations }) => integrations.integrations)
  const directoryIntegration = useMemo(
    () =>
      resolveDirectoryIntegrationForModal({
        selectedIntegration: selectedIntegrationFromPage,
        selectedIntegrationType: selectedIntegrationTypeFromPage,
        reduxSelectedIntegration,
        integrations: reduxIntegrations,
        customerId: selectedCustomer?.id,
      }),
    [
      selectedIntegrationFromPage,
      selectedIntegrationTypeFromPage,
      reduxSelectedIntegration,
      reduxIntegrations,
      selectedCustomer?.id,
    ],
  )
  const directoryApplicationName = directoryIntegration?.IntegrationApplication?.application_name

  const freshEmployee = useFreshEmployeeData({
    employee: tableRowEmployee,
    show,
    customer: selectedCustomer,
    isStaleData: (emp) => !(emp?.streetAddress || emp?.city || emp?.department || emp?.job_title),
    integrationApplicationName: directoryApplicationName,
  })
  const employee = useMemo(() => freshEmployee ?? {}, [freshEmployee])
  const isGws = isGwsDirectoryEmployee(employee)
  const isIntegrated = useMemo(
    () => !!(employee?.employeeIdentifier || employee?.employeeIntegrationInfo),
    [employee?.employeeIdentifier, employee?.employeeIntegrationInfo],
  )
  const isOffice365 = isIntegrated && !isGws
  const assignedLicenseIds = useMemo(() => getAssignedLicenseIds(employee, { isGws }), [employee, isGws])
  const currentEmployeeId = useMemo(
    () => employee?.id ?? employee?.employeeId ?? tableRowEmployee?.id ?? tableRowEmployee?.employeeId,
    [employee?.id, employee?.employeeId, tableRowEmployee?.id, tableRowEmployee?.employeeId],
  )

  const { allEmployees } = useAllEmployees({ allEmployees: propAllEmployees, selectedCustomer, show })
  const showMicrosoftDirectoryPanels = isIntegrated && !isGws
  const gwsLicenseCatalog = useGwsLicenseCatalog({
    show,
    isGws,
    selectedCustomer,
    employee,
  })
  const {
    customerLicenses,
    syncCustomerLicenses,
    isFetchingCustomerLicenses,
    errorFetchingCustomerLicenses,
    isSyncingCustomerLicenses,
  } = useCustomerLicenses({
    customerId: selectedCustomer?.id,
    autoFetch: Boolean(show && showMicrosoftDirectoryPanels),
  })
  const licensesForForm = isGws ? gwsLicenseCatalog : customerLicenses

  // Fetch groups if not provided via props
  const { fetchGroups, groups } = useGroupsFetch()

  const allEmployeesGroupsFinal =
    allEmployeesGroups.length > 0 ? allEmployeesGroups : adaptGroupsToStandardFormat(groups)

  useEffect(() => {
    if (show && selectedCustomer?.id && allEmployeesGroups.length === 0 && showMicrosoftDirectoryPanels) {
      fetchGroups({ customerId: selectedCustomer.id })
    }
  }, [show, selectedCustomer?.id, allEmployeesGroups.length, showMicrosoftDirectoryPanels, fetchGroups])

  const modalTabs = useMemo(() => getEditModalTabs({ isIntegrated, isGws }), [isIntegrated, isGws])

  const employeeEmailOptions = useMemo(() => {
    if (!allEmployees?.length) return []
    return allEmployees
      .filter((emp) => {
        if (!emp || emp.ignored || !emp.email) return false
        const empId = emp.id || emp.employeeId
        return empId != null && Number(empId) !== Number(currentEmployeeId)
      })
      .map((emp) => {
        const empId = emp.id || emp.employeeId
        const fullName = getEmployeeFullName(emp)
        return {
          label: fullName ? `${fullName} (${emp.email})` : emp.email,
          value: Number(empId),
        }
      })
      .filter((opt) => opt.value != null && !Number.isNaN(opt.value))
      .sort((a, b) => a?.label?.localeCompare(b?.label))
  }, [allEmployees, currentEmployeeId])

  const customTitle = useMemo(
    () => (
      <div
        className="d-flex align-items-center employee-header-title"
        style={{
          gap: '1rem',
          height: '3.125rem',
          marginBottom: '1.5rem',
          boxSizing: 'border-box',
        }}
      >
        {/* Profile Picture / Avatar */}
        <div
          className="d-flex align-items-center justify-content-center employee-header-avatar fw-bold"
          style={{
            width: '3.125rem',
            height: '3.125rem',
            borderRadius: '50%',
            backgroundColor: 'var(--bs-primary)',
            color: 'var(--bs-white)',
            fontSize: '1.2rem',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {getEmployeeInitials(employee)}
        </div>

        {/* Name and Email */}
        <div
          className="flex-grow"
          style={{
            minWidth: 0,
            height: '3.125rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <div className="d-flex align-items-center" style={{ height: '2rem', lineHeight: '2rem' }}>
            {(employee.isUserSigninEnabled === false || employee.enabled === false) && (
              <span className="suspended me-2" style={{ lineHeight: '2rem' }}>
                <i className="fa fa-ban" />
              </span>
            )}
            <span className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '2rem', display: 'inline-block' }}>
              {getEmployeeFullName(employee)}
            </span>
            {employee.ignored && (
              <span className="ms-2 badge badge-secondary" style={{ lineHeight: '2rem' }}>
                Ignored
              </span>
            )}
          </div>

          <div
            className="d-flex align-items-center"
            style={{
              gap: '0.5rem',
              height: '1.125rem',
              lineHeight: '1.125rem',
            }}
          >
            <span
              className="font-color-2"
              style={{ fontSize: '0.95rem', lineHeight: '1.125rem', display: 'inline-block' }}
            >
              {employee.email}
            </span>
            {employee.email && (
              <CopyButton textToCopy={employee.email} className="d-inline-block" style={{ lineHeight: '1.125rem' }} />
            )}
          </div>
        </div>
      </div>
    ),
    [employee],
  )

  useEffect(() => {
    if (!show) {
      lastEmployeeIdRef.current = null
      lastEmployeeDataRef.current = null
      setFormInitialValues({})
      setLicenseBaseline([])
      setSubmitStatus('idle')
      setSubmitError(null)
      return
    }

    const sourceEmployee = employee && Object.keys(employee).length > 0 ? employee : tableRowEmployee || {}
    const prevEmployee = lastEmployeeDataRef.current
    const employeeIdChanged = currentEmployeeId != null && currentEmployeeId !== lastEmployeeIdRef.current
    const contactOrJobChanged =
      prevEmployee && REINIT_COMPARE_FIELDS.some((field) => valueChanged(sourceEmployee[field], prevEmployee[field]))
    const shouldReinit = employeeIdChanged || contactOrJobChanged || (!lastEmployeeIdRef.current && currentEmployeeId)

    if (shouldReinit && (currentEmployeeId != null || Object.keys(sourceEmployee).length > 0)) {
      const nextInitialValues = getInitialValues(sourceEmployee, { isGws })
      setFormInitialValues(nextInitialValues)
      setLicenseBaseline(nextInitialValues[FORM_FIELDS.LICENSES] || [])
      lastEmployeeIdRef.current = currentEmployeeId
      lastEmployeeDataRef.current = REINIT_COMPARE_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field]: sourceEmployee[field] }),
        {},
      )
    }
  }, [show, employee, tableRowEmployee, currentEmployeeId, isGws])

  const initialValues = formInitialValues

  const validationSchema = useMemo(() => getValidationSchema(intl), [intl])

  const runHubTask = useCallback(
    async (actions, tasktype, action) => {
      try {
        const hubTask = {
          actions,
          tasktype,
          action,
          customerId: selectedCustomer?.id,
          organizationId: selectedCustomer?.organization,
          userId: currentUser?.id,
        }
        const taskResponse = await createHubTask(hubTask)
        const taskId = taskResponse?.data != null ? taskResponse.data.taskId : undefined
        if (taskId) await pollTaskStatus(taskId, { retries: 10, timeout: 3000 })
      } catch (error) {
        console.error('Error running hub task:', error)
        throw error
      }
    },
    [selectedCustomer?.id, selectedCustomer?.organization, currentUser?.id],
  )

  const finishSuccess = useCallback(() => {
    setSubmitStatus('success')
    setToastInfo({ show: true, type: 'GENERAL.ACTION_SUCCESS' })
    onUpdate?.()
    onHide()
  }, [setToastInfo, onUpdate, onHide])

  const handleLicensesApplied = useCallback((licenseIds) => {
    setLicenseBaseline(Array.isArray(licenseIds) ? licenseIds : [])
  }, [])

  const finishError = useCallback(
    (errorMessage) => {
      setSubmitError(errorMessage)
      setSubmitStatus('error')
      setToastInfo({ show: true, type: 'GENERAL.ACTION_FAILED' })
    },
    [setToastInfo],
  )

  const handleSubmit = async (values) => {
    try {
      setSubmitStatus('loading')
      setSubmitError(null)

      const isEmailEditable = isIntegrated ? !employee?.onPremisesSyncEnabled : true
      const payload = toApiPayload(values, initialValues, isIntegrated, {
        isEmailEditable,
        includeMicrosoftDirectoryChanges: isIntegrated && !isGws,
        licenseBaseline,
      })

      if (!isIntegrated) {
        if (Object.keys(payload.sql).length > 0) {
          await updateEmployee({ id: employee.id, ...payload.sql })
        }
        finishSuccess()
        return
      }

      const hasGraphApiChanges =
        !isGws && payload.graphApiProperties && Object.keys(payload.graphApiProperties).length > 0

      if (isGws && typeof payload.sql.email === 'string') {
        const nextEmail = payload.sql.email.trim()
        await submitGwsPrimaryEmailChange({
          employee,
          nextEmail,
          selectedCustomer,
          userId: currentUser?.id,
          intl,
        })
        // The hub subscriber stores the address Google Directory has now.
        delete payload.sql.email
      }

      if (Object.keys(payload.sql).length > 0) {
        await updateEmployee({ id: employee.id, ...payload.sql })
      }

      if (hasGraphApiChanges && employee.employeeIdentifier && selectedCustomer) {
        await updateUserProperties({
          customerId: selectedCustomer.id,
          organizationId: selectedCustomer.organization,
          userId: currentUser?.id,
          employeeIdentifier: employee.employeeIdentifier,
          userProperties: payload.graphApiProperties,
        })
      }

      if (payload.groupsChanges && !isGws && employee.employeeIdentifier && selectedCustomer) {
        const { m365Groups, securityGroups } = payload.groupsChanges

        if (m365Groups && (m365Groups.toBeAdded.length > 0 || m365Groups.toBeRemoved.length > 0)) {
          await runHubTask(
            {
              employeeIdentifiers: [employee.employeeIdentifier],
              groupsInfo: { toBeAdded: m365Groups.toBeAdded, toBeRemoved: m365Groups.toBeRemoved },
            },
            TASK_TYPES.manageM365Groups,
            TASK_ACTIONS.update,
          )
        }
        if (securityGroups && (securityGroups.toBeAdded.length > 0 || securityGroups.toBeRemoved.length > 0)) {
          await runHubTask(
            {
              employeeIdentifiers: [employee.employeeIdentifier],
              groupsInfo: {
                toBeAdded: securityGroups.toBeAdded,
                toBeRemoved: securityGroups.toBeRemoved,
              },
            },
            TASK_TYPES.manageSecurityGroups,
            TASK_ACTIONS.update,
          )
        }
      }

      const licenseChanges = payload.licensesChanges
      const licenseIdsToAdd = licenseChanges?.toBeAdded || []
      const licenseIdsToRemove = licenseChanges?.toBeRemoved || []
      const hasLicenseChanges = selectedCustomer && (licenseIdsToAdd.length > 0 || licenseIdsToRemove.length > 0)

      if (hasLicenseChanges) {
        if (isGws) {
          const gwsLicenseIdsToAdd = getGwsLicenseSkuIds(licenseIdsToAdd)
          const gwsLicenseIdsToRemove = getGwsLicenseSkuIds(licenseIdsToRemove)
          const gwsLicenseUserId = getGwsLicenseUserId(employee)
          const gwsDirectoryUserId = getGwsDirectoryUserId(employee)

          if (gwsLicenseIdsToAdd.length > 0 || gwsLicenseIdsToRemove.length > 0) {
            if (!gwsLicenseUserId && !gwsDirectoryUserId) {
              throw new Error('License update failed')
            }
            const taskResponse = await createTask(
              selectedCustomer.id,
              GWS,
              GWS_ACTIONS.GWS_MANAGE_LICENSE,
              currentUser?.id,
              {
                employeeEmail: gwsLicenseUserId || gwsDirectoryUserId,
                employeeIdentifier: gwsDirectoryUserId,
                licenseInfo: {
                  toBeAdded: gwsLicenseIdsToAdd,
                  toBeRemoved: gwsLicenseIdsToRemove,
                  conflictReplacementSkuIds: licenseChanges.conflictReplacementSkuIds || [],
                },
              },
            )
            if (taskResponse?.data?.errorMessage) {
              throw new Error(taskResponse.data.errorMessage)
            }
            const taskId = taskResponse?.data?.taskId
            if (taskId) {
              const { error } = await monitorTask(selectedCustomer.id, taskId, 60)
              if (error) {
                throw new Error(error.rawMessage || error.StatusMessage || 'License update failed')
              }
            }
          }
        } else {
          const isM365License = (id) => typeof id === 'string' && id.includes('-')
          const m365ToAdd = licenseIdsToAdd.filter(isM365License)
          const m365ToRemove = licenseIdsToRemove.filter(isM365License)
          const moduleToAdd = licenseIdsToAdd.filter((id) => !isM365License(id))
          const moduleToRemove = licenseIdsToRemove.filter((id) => !isM365License(id))

          const licensePromises = []

          if (isOffice365 && (m365ToAdd.length > 0 || m365ToRemove.length > 0) && employee.employeeIdentifier) {
            licensePromises.push(
              (async () => {
                const taskPayload = {
                  customerId: selectedCustomer.id,
                  userId: currentUser?.id,
                  integrationType: 'Office365',
                  mgmTaskId: 'M365_SECURE_PARTIAL',
                  parameters: {
                    taskName: 'M365_ADD_USER',
                    actions: {
                      employeeIdentifier: employee.employeeIdentifier,
                      firstName: employee.first_name || employee.firstName,
                      lastName: employee.last_name || employee.lastName,
                      displayName: employee.display_name || employee.displayName,
                      email: employee.email,
                      department: employee.department,
                      licenseInfo: {
                        toBeAdded: m365ToAdd,
                        toBeRemoved: m365ToRemove,
                      },
                    },
                  },
                }
                const taskResponse = await createMgmTask(taskPayload)
                const taskId = taskResponse?.data != null ? taskResponse.data.taskId : undefined
                if (taskId) {
                  const { error } = await monitorTask(selectedCustomer.id, taskId, 60)
                  if (error) {
                    throw new Error(error.rawMessage || error.StatusMessage || 'License update failed')
                  }
                }
                if (syncCustomerLicenses) {
                  await syncCustomerLicenses({
                    customerId: selectedCustomer.id,
                    organizationId: selectedCustomer.organization,
                    userId: currentUser?.id,
                  })
                }
              })(),
            )
          }

          if (moduleToAdd.length > 0 || moduleToRemove.length > 0) {
            licensePromises.push(
              (async () => {
                if (moduleToRemove.length > 0) {
                  await Promise.all(
                    moduleToRemove.map((licenseId) =>
                      unassignLicenseFromEmployees(selectedCustomer, [employee.id], licenseId),
                    ),
                  )
                }
                if (moduleToAdd.length > 0) {
                  await assignLicenseToEmployees(selectedCustomer, [employee.id], moduleToAdd)
                }
              })(),
            )
          }

          if (licensePromises.length > 0) {
            try {
              await Promise.all(licensePromises)
            } catch (error) {
              console.error('Error updating licenses:', error)
              throw error
            }
          }
        }
      }

      if (payload.powershellAccessChanged !== undefined && !isGws && employee.employeeIdentifier && selectedCustomer) {
        await runHubTask(
          {
            employeeIdentifiers: [employee.employeeIdentifier],
            remotePowerShellEnabled: payload.powershellAccessChanged,
          },
          TASK_TYPES.managePowershellAccess,
          TASK_ACTIONS.update,
        )
      }

      finishSuccess()
    } catch (error) {
      console.error('Error updating employee:', error)
      finishError(error?.response?.data?.message || error?.message || 'GENERAL.ACTION_FAILED')
    }
  }

  const formKey = useMemo(() => currentEmployeeId ?? 'new', [currentEmployeeId])

  return (
    <Formik
      key={formKey}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <ModalSidebar
          show={show}
          onHide={onHide}
          size="lg"
          title={customTitle}
          isLoading={isSubmitting || submitStatus === 'loading'}
          headerBackButtonAction={headerBackButtonAction}
        >
          <EditFormContent
            modalSubmitStatusProp={submitStatus}
            submitErrorProp={submitError}
            employeeEmailOptionsProp={employeeEmailOptions}
            modalTabsProp={modalTabs}
            employeeProp={employee}
            intlProp={intl}
            isIntegratedProp={isIntegrated}
            isGwsProp={isGws}
            allEmployeesGroupsProp={allEmployeesGroupsFinal}
            customerLicensesProp={licensesForForm}
            isFetchingCustomerLicensesProp={isFetchingCustomerLicenses}
            errorFetchingCustomerLicensesProp={errorFetchingCustomerLicenses}
            syncCustomerLicensesProp={syncCustomerLicenses}
            isSyncingCustomerLicensesProp={isSyncingCustomerLicenses}
            assignedLicenseIdsProp={assignedLicenseIds}
            currentEmployeeIdProp={currentEmployeeId}
            onLicensesAppliedProp={handleLicensesApplied}
          />
        </ModalSidebar>
      )}
    </Formik>
  )
}

export default injectIntl(EditEmployeeDataModal)
